<script setup>
import { ref, defineProps, defineEmits, watch } from "vue";
import * as adminStore from "@/services/adminService";
import DefaultButton from "@/components/utils/DefaultButton.vue";
import { customToast } from "@/globals/utils/customToast"; // Importa customToast

// Definir propiedades recibidas desde el componente padre
const props = defineProps({
  showModal: {
    type: Boolean,
    required: true,
  },
  carId: {
    type: String,
    required: true,
  },
});

// Definir eventos emitidos al componente padre
const emit = defineEmits(["close", "updateCars"]);

const selectedDriver = ref("");
const drivers = ref([]);

// Obtener conductores sin asignar
const fetchDrivers = async () => {
  try {
    const response = await adminStore.driversUnassigned();
    drivers.value = response;
  } catch (err) {
    console.error("Error fetching unassigned drivers:", err);
  }
};

// Asignar conductor a un carro
const assignDriver = async () => {
  if (!selectedDriver.value) return;

  try {
    await adminStore.assignDriver(props.carId, selectedDriver.value);

    // Mostrar mensaje de éxito
    customToast("Driver assigned successfully!", "success");

    // Resetear selección
    selectedDriver.value = "";

    // Cerrar modal y actualizar la lista de carros en el padre
    emit("close");
    emit("updateCars");
  } catch (err) {
    console.error("Error assigning driver:", err);
    customToast("Failed to assign driver", "error");
  }
};

// Cargar conductores al abrir el modal
watch(() => props.showModal, (newValue) => {
  if (newValue) fetchDrivers();
});
</script>

<template>
  <transition name="fade">
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40" @click.self="$emit('close')">
      <transition name="modal">
        <div class="bg-white dark:bg-gray-900 p-8 rounded-lg shadow-lg w-full max-w-[500px]" v-if="showModal">
          <h2 class="text-2xl font-bold mb-4 dark:text-gray-300">Assign Driver</h2>
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <label for="driver" class="block text-sm font-medium text-gray-700 dark:text-gray-200">Driver</label>
              <select
                  id="driver"
                  v-model="selectedDriver"
                  class="w-3/4 p-2 border border-gray-300 dark:bg-gray-700 dark:border-gray-400 dark:text-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-700 transition duration-200 ease-in-out"
              >
                <option value="" disabled>Select a driver</option>
                <option v-for="driver in drivers" :key="driver._id" :value="driver._id">{{ driver.name }}</option>
              </select>
            </div>
            <div class="flex items-center justify-between">
              <DefaultButton title="Cancel" @click="$emit('close')" />
              <DefaultButton title="Assign" @click="assignDriver" :disabled="!selectedDriver" />
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<style scoped>
/* Fade-in animation for overlay */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Slide-up animation for modal */
.modal-enter-active, .modal-leave-active {
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}
.modal-enter, .modal-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
